import { ThemeProvider } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'shared/hooks';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import defaultTheme from 'shared/defaultTheme';
import { ToolLoader } from 'shared/components';
import { INIT } from 'shared/wizard/actions';
import ToolContainer from 'shared/wizard/ToolContainer';
import { getSettings } from '../helpers';
import analytics from 'shared/utility/analytics';
import lazyWithRetry from "shared/utility/lazyWithRetry";

const outputResultsPromise = import(
  /* webpackChunkName: "topafslag-output-results" */
  './OutputResults'
);
const wizardCurrentPromise = import(
  /* webpackChunkName: "topafslag-wizard-current" */
  './WizardCurrent'
);
const wizardLandingPromise = import(
  /* webpackChunkName: "topafslag-wizard-landing" */
  './WizardLanding'
);
const wizardOriginalPromise = import(
  /* webpackChunkName: "topafslag-wizard-original" */
  './WizardOriginal'
);
const wizardResultsPromise = import(
  /* webpackChunkName: "topafslag-wizard-results" */
  './WizardResults'
);

const OutputResults = lazyWithRetry(() => outputResultsPromise);
const WizardCurrent = lazyWithRetry(() => wizardCurrentPromise);
const WizardLanding = lazyWithRetry(() => wizardLandingPromise);
const WizardOriginal = lazyWithRetry(() => wizardOriginalPromise);
const WizardResults = lazyWithRetry(() => wizardResultsPromise);

const App = () => {
  const { app, settings, framed, pages } = useSelector(state => state);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(defaultTheme);
  const base = `${app}${framed ? '/framed' : ''}`;

  useEffect(() => {
    navigate(`/${app}${framed ? '/framed' : ''}/${pages[0] + location.search}`, {
      state: { page: pages[0] },
      replace: true,
    });
  }, [framed]);

  useEffect(() => {
    analytics.sendPageview(location.pathname)
  }, [location.pathname])

  useEffect(() => {
    getSettings(res => {
      const settings = res.data || {};
      dispatch({
        type: INIT,
        payload: { settings },
      });
      setTheme(theme => ({ ...theme, ...res.data }));
    });
  }, []);

  if (!settings) return <ToolLoader show />;
  return (
    <ThemeProvider theme={theme}>
      <ToolContainer
        Input={
          <Routes>
            <Route path={`/${base}/original-situation`} element={<WizardOriginal />} />
            <Route path={`/${base}/current-situation`} element={<WizardCurrent />} />
            <Route path={`/${base}/final`} element={<WizardResults />} />
            <Route path={`/${base}/landing`} element={<WizardLanding />} />
          </Routes>
        }
        Output={
          <Routes>
            <Route path={`/${base}/final`} element={<OutputResults />} />
          </Routes>
        }
        customDisclaimer={
          <>
            <p>
              Ondanks de constante zorg en aandacht die wij besteden aan de samenstelling van deze tool en de daarin
              gevraagde gegevens, kunnen wij niet instaan voor de volledigheid, juistheid of voortdurende actualiteit
              van de gegevens. Wij aanvaarden dan ook geen aansprakelijkheid voor enigerlei directe of indirecte schade,
              van welke aard ook, die voortvloeit uit of in enig opzicht verband houdt met het gebruik van deze tool.
            </p>
            <p>
              Voor het berekenen van de besparing maken wij gebruik van de rentes en tariefklassen die je verstrekker op
              dit moment hanteert. Deze kunnen afwijken van de rentes en tariefklassen die de bank hanteerde op het
              moment dat je huidige rentevastperiode inging.
            </p>
            <p>
              Wij bepalen een mogelijke besparing op basis van een annuïtair product met een rentevaste periode van 10
              jaar. Wij houden geen rekening met rentedalingen die je voor dit moment hebt gehad, ook niet als de
              verstrekker deze automatisch doorvoert.
            </p>
            <p>Je adviseur kan een exacte berekening voor je maken.</p>
          </>
        }
      />
    </ThemeProvider>
  );
};

export default App;
